// Markup for the page. Copied from irobot-site/api/src/boundary/html.ts. A
// plain string is never Html: the `html` tag escapes every value put into it
// unless that value is already Html, so a player's name or note can never
// become markup (guidelines §10a). The marker is a symbol only this module
// can see, so an Html value cannot be faked at run time either.

import type { Brand } from "../domain/brand.ts"

const HTML: unique symbol = Symbol("html")
type HtmlBox = { readonly [HTML]: string }
export type Html = Brand<HtmlBox, "Html">

const box = (value: string): Html => ({ [HTML]: value }) as Html

const isHtml = (value: unknown): value is Html => typeof value === "object" && value !== null && HTML in value

export type HtmlPart = Html | string | number | readonly Html[]

const escapeChar = (c: string): string => {
  switch (c) {
    case "&":
      return "&amp;"
    case "<":
      return "&lt;"
    case ">":
      return "&gt;"
    case '"':
      return "&quot;"
    default:
      return "&#39;"
  }
}

export const escapeText = (s: string): string => s.replace(/[&<>"']/g, escapeChar)

const partToString = (part: HtmlPart): string => {
  if (typeof part === "number") return String(part)
  if (typeof part === "string") return escapeText(part)
  if (isHtml(part)) return part[HTML]
  return part.map((p) => p[HTML]).join("")
}

export const html = (strings: TemplateStringsArray, ...parts: readonly HtmlPart[]): Html =>
  box(strings.reduce((out, chunk, i) => out + chunk + (i < parts.length ? partToString(parts[i] ?? "") : ""), ""))

/** The one unsafe door: text that is already markup. Only for our own constant drawings. */
export const unsafeRawHtml = (value: string): Html => box(value)

export const renderHtml = (value: Html): string => value[HTML]

export const joinHtml = (parts: readonly Html[]): Html => box(parts.map(renderHtml).join(""))

export const EMPTY_HTML: Html = box("")
